import React, { FunctionComponent } from 'react';
import {
  Alert,
  Box,
  Button,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';

import { ColDef } from 'ag-grid-community';

import OverflowTooltip from '../../shared/OverflowTooltip';

import Table from '../../shared/Table';

import { IModelOption, ISubscription } from '../ManageInterModelSubscriptions';

interface IReplicateSubscriptionsProps {
  modelName: string;
  instanceName: string;
  subscriptionsToReplicate: ISubscription[];
  targetInstanceOptions: IModelOption[];
  selectedTargetInstance: number | '';
  replicateResult: { created: number[]; skipped: any[] } | null;
  onSelectedTargetInstanceChange: (value: number | '') => void;
  onBackToList: () => void;
}

const subscriptionColumns: ColDef[] = [
  {
    field: 'scenarioTag',
    headerName: 'Subscription Scenario Tag',
    flex: 1,
    minWidth: 300,
    // CLD-3040: full tag name on hover when it's clipped.
    cellRenderer: OverflowTooltip,
  },
  {
    field: 'status',
    headerName: 'Subscription Status',
    flex: 1,
    minWidth: 200,
  },
];

const ReplicateSubscriptions: FunctionComponent<
  IReplicateSubscriptionsProps
> = ({
  modelName,
  instanceName,
  subscriptionsToReplicate,
  targetInstanceOptions,
  selectedTargetInstance,
  replicateResult,
  onSelectedTargetInstanceChange,
  onBackToList,
}) => {
  return (
    <Box sx={{ mb: 3 }}>
      {/* Back Button */}
      <Box sx={{ mb: 2 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={onBackToList}
          sx={{ textTransform: 'none' }}
        >
          Back to Subscriptions
        </Button>
      </Box>

      {/* Left Section: Source Model Info */}
      <Box
        sx={{ display: 'flex', gap: 4, mb: 3, justifyContent: 'space-between' }}
      >
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Model
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {modelName}
            </Typography>
          </Box>

          <Box>
            <Typography variant="caption" color="text.secondary">
              Source Model Instance
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {instanceName}
            </Typography>
          </Box>
        </Box>
        <Box>
          <FormControl size="small" sx={{ minWidth: 270 }}>
            <InputLabel>Select a Target model instance</InputLabel>
            <Select
              value={selectedTargetInstance}
              onChange={(e) =>
                onSelectedTargetInstanceChange(e.target.value as number)
              }
              label="Select a Target model instance"
            >
              {targetInstanceOptions.map((option) => (
                <MenuItem key={option.id} value={option.id}>
                  {option.Name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

      {replicateResult && (
        <Box sx={{ mb: 2 }}>
          <Alert
            severity={replicateResult.skipped.length ? 'warning' : 'success'}
          >
            {replicateResult.created.length} subscription(s) replicated
            {replicateResult.skipped.length > 0 && (
              <>
                . Skipped:{' '}
                {replicateResult.skipped
                  .map(
                    (item) =>
                      `${item.tagName || item.scenarioLinkTagId} (${item.reason})`
                  )
                  .join(', ')}
              </>
            )}
          </Alert>
        </Box>
      )}

      {/* Subscriptions to be Replicated Table */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
          Subscription Scenario Tags to be replicated
        </Typography>
        {subscriptionsToReplicate && subscriptionsToReplicate.length > 0 ? (
          <Table
            id="replicate-subscriptions-table"
            isLoading={false}
            isSuccess
            pagination={false}
            rowCount={subscriptionsToReplicate.length}
            rowData={subscriptionsToReplicate}
            columnDefs={subscriptionColumns}
            defaultColDef={{
              suppressAutoSize: true,
              suppressMenu: true,
              suppressSizeToFit: true,
            }}
          />
        ) : (
          <Box textAlign="center" py={3}>
            <Typography variant="body2" color="text.secondary">
              No subscriptions to replicate.
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ReplicateSubscriptions;
